import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { getPedidoById } from "../../../services/usuarios_tienda/pedidos";
import { fileURL } from "../../../utils/fileURL";
import { formatearPrecio } from "../../../utils/moneda";
import "./OrderReceiptModal.css";

const formatDate = (dt) => {
  if (!dt) return "—";
  // La API devuelve la fecha en UTC (con "Z"); la mostramos en la zona
  // horaria local para que coincida con el día real del usuario.
  const d = new Date(dt);
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  }
  const raw = String(dt).split("T")[0];
  return raw ? raw.split("-").reverse().join("/") : "—";
};

function parseEstadoPago(raw) {
  const v = String(raw || "").trim().toUpperCase();
  if (v === "PAGADO" || v === "PAID" || v === "COMPLETED" || v === "COMPLETADO")
    return "Pagado";
  if (v === "AUTORIZADO" || v === "AUTHORIZED") return "Autorizado";
  if (v === "FALLIDO" || v === "FAILED") return "Fallido";
  if (v === "REEMBOLSADO" || v === "REFUNDED") return "Reembolsado";
  return "No pagado";
}

function OrderReceiptModal({ pedidoId, onClose, configuracionTienda, storeInfo }) {
  const [pedido, setPedido] = useState(null);
  const [loading, setLoading] = useState(false);

  const fmtMoney = useCallback(
    (v) =>
      formatearPrecio(v, configuracionTienda, { espacioEntreSimbolo: true }) ||
      "—",
    [configuracionTienda]
  );

  useEffect(() => {
    if (!pedidoId) return undefined;

    let alive = true;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    (async () => {
      setLoading(true);
      setPedido(null);
      try {
        const data = await getPedidoById(pedidoId);
        if (alive) setPedido(data || null);
      } catch (e) {
        console.error("Error cargando pedido para recibo:", e);
        if (alive) setPedido(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [pedidoId, onClose]);

  const clienteNombre = useMemo(() => {
    const full = `${pedido?.nombre_cliente || ""} ${
      pedido?.apellido_cliente || ""
    }`.trim();
    return full || "—";
  }, [pedido?.nombre_cliente, pedido?.apellido_cliente]);

  const clienteTelefono = useMemo(() => {
    const code = String(pedido?.codigo_telefono || "").trim();
    const phone = String(pedido?.telefono_cliente || "").trim();
    return `${code} ${phone}`.trim() || "—";
  }, [pedido?.codigo_telefono, pedido?.telefono_cliente]);

  const items = useMemo(() => {
    if (Array.isArray(pedido?.items)) return pedido.items;
    if (Array.isArray(pedido?.detalles)) return pedido.detalles;
    return [];
  }, [pedido]);

  const totalItems = useMemo(
    () => items.reduce((sum, d) => sum + Number(d?.cantidad || 0), 0),
    [items]
  );

  const closeOverlay = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const handlePrint = () => {
    const prevTitle = document.title;
    document.title = `Recibo-Pedido-${pedidoId}`;
    window.print();
    // Restaura el título (algunos navegadores lo usan como nombre del PDF).
    setTimeout(() => {
      document.title = prevTitle;
    }, 300);
  };

  if (!pedidoId) return null;

  const storeName =
    storeInfo?.nombre_comercial || storeInfo?.nombre || "Tienda";
  const storeLogo = storeInfo?.url_logo ? fileURL(storeInfo.url_logo) : null;

  return createPortal(
    <div className="orcpt-overlay" onMouseDown={closeOverlay}>
      <div
        className="orcpt-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Recibo del pedido"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="orcpt-toolbar">
          <div className="orcpt-toolbar__title">
            Recibo · Pedido #{pedidoId}
          </div>
          <div className="orcpt-toolbar__actions">
            <button
              type="button"
              className="orcpt-btn orcpt-btn--primary"
              onClick={handlePrint}
              disabled={loading || !pedido}
            >
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M6 9V2h12v7" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" rx="1" />
              </svg>
              Imprimir
            </button>
            <button type="button" className="orcpt-btn" onClick={onClose}>
              Cerrar
            </button>
          </div>
        </header>

        <div className="orcpt-preview">
          {loading ? (
            <div className="orcpt-loading">Cargando recibo…</div>
          ) : !pedido ? (
            <div className="orcpt-loading">No se pudo cargar el pedido.</div>
          ) : (
            <div className="orcpt-sheet orcpt-print-root">
              {/* Encabezado */}
              <div className="orcpt-head">
                <div className="orcpt-head__brand">
                  {storeLogo ? (
                    <img
                      className="orcpt-head__logo"
                      src={storeLogo}
                      alt={storeName}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : null}
                  <div>
                    <div className="orcpt-head__store">{storeName}</div>
                    <div className="orcpt-head__doc">Recibo de compra</div>
                  </div>
                </div>
                <div className="orcpt-head__meta">
                  <div className="orcpt-head__order">Pedido #{pedidoId}</div>
                  <div className="orcpt-head__row">
                    <span>Fecha</span>
                    <b>{formatDate(pedido?.fecha_creado)}</b>
                  </div>
                  <div className="orcpt-head__row">
                    <span>Pago</span>
                    <b>{parseEstadoPago(pedido?.estado_pago)}</b>
                  </div>
                </div>
              </div>

              {/* Cliente */}
              <div className="orcpt-parties">
                <section className="orcpt-box">
                  <h4 className="orcpt-box__title">Cliente</h4>
                  <div className="orcpt-box__name">{clienteNombre}</div>
                  <div className="orcpt-box__line">{pedido?.correo || "—"}</div>
                  <div className="orcpt-box__line">Tel: {clienteTelefono}</div>
                </section>
              </div>

              {/* Productos */}
              <table className="orcpt-table">
                <thead>
                  <tr>
                    <th className="orcpt-th-prod">Producto</th>
                    <th className="orcpt-th-num">Cant.</th>
                    <th className="orcpt-th-num">Unitario</th>
                    <th className="orcpt-th-num">Core</th>
                    <th className="orcpt-th-num">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="orcpt-empty">
                        Sin productos.
                      </td>
                    </tr>
                  ) : (
                    items.map((d, idx) => {
                      const coreUnit = Number(
                        d?.core_charge_unit ?? d?.core_charge ?? 0
                      );
                      const key =
                        d?.pedido_detalles_id ?? `${d?.producto_id ?? "p"}-${idx}`;
                      return (
                        <tr key={key}>
                          <td className="orcpt-td-prod">
                            <div className="orcpt-prod-name">
                              {d?.nombre_producto || "—"}
                            </div>
                            <div className="orcpt-prod-sku">
                              SKU {d?.no_parte || "—"}
                            </div>
                          </td>
                          <td className="orcpt-td-num">{d?.cantidad ?? "—"}</td>
                          <td className="orcpt-td-num">
                            {fmtMoney(d?.precio_unitario)}
                          </td>
                          <td className="orcpt-td-num">
                            {coreUnit ? fmtMoney(coreUnit) : "—"}
                          </td>
                          <td className="orcpt-td-num orcpt-strong">
                            {fmtMoney(d?.subtotal ?? d?.total)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              {/* Totales */}
              <div className="orcpt-summary">
                <div className="orcpt-summary__items">
                  {totalItems} artículo{totalItems === 1 ? "" : "s"} en total
                </div>
                <div className="orcpt-totals">
                  <div className="orcpt-totals__row">
                    <span>Subtotal</span>
                    <span>{fmtMoney(pedido?.subtotal)}</span>
                  </div>
                  <div className="orcpt-totals__row">
                    <span>Envío</span>
                    <span>{fmtMoney(pedido?.costo_envio)}</span>
                  </div>
                  {Number(pedido?.core_charge_total || 0) > 0 && (
                    <div className="orcpt-totals__row">
                      <span>Core charge</span>
                      <span>{fmtMoney(pedido?.core_charge_total)}</span>
                    </div>
                  )}
                  <div className="orcpt-totals__row orcpt-totals__row--grand">
                    <span>Total</span>
                    <span>{fmtMoney(pedido?.total_factura)}</span>
                  </div>
                </div>
              </div>

              {/* Pie */}
              <div className="orcpt-foot">
                <div className="orcpt-thanks">¡Gracias por su compra!</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default OrderReceiptModal;
